// Settings.tsx
import React, {useEffect, useState} from 'react';
import {Calendar, Key} from 'lucide-react';
import { ChangePassword } from './ChangePassword.tsx';
import { SetOutOfOffice } from './SetOutOfOffice.tsx';
import Modal from "../../../components/Modal.tsx";
import {Field, Label, Switch} from '@headlessui/react';
import {useAppraiserApi} from "../../../context/ApiContext.tsx";
import {AppraiserProfileTabProps} from "./AppraiserProfile.tsx";

export const Settings: React.FC<AppraiserProfileTabProps> = ({appraiserInfo, refreshAppraiserData}) => {
    const [haveTrainee, setHaveTrainee] = useState<boolean>(false);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [isOOOModalOpen, setIsOOOModalOpen] = useState(false);
    const [outOfOffice, setOutOfOffice] = useState({
        startDate:'',
        endDate: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const appraiserApi = useAppraiserApi();

    useEffect(() => {
        setOutOfOffice(
            {
                startDate: appraiserInfo?.oooStartDate
                    ? new Date(appraiserInfo?.oooStartDate).toISOString().split('T')[0]
                    : '',
                endDate: appraiserInfo?.oooEndDate
                    ? new Date(appraiserInfo?.oooEndDate).toISOString().split('T')[0]
                    : ''
            }
        )
        setHaveTrainee(!appraiserInfo?.noTrainee);
    }, [appraiserInfo]);



    const handleTraineeToggle = async () => {
        if (!appraiserInfo?.id) {
            return;
        }

        setIsLoading(true);
        await appraiserApi.updateNoTrainee({appraiserId: appraiserInfo.id, hasTrainee: !haveTrainee})
            .then(() => refreshAppraiserData())
            .catch(console.log)
            .finally(() => setIsLoading(false));
    };

    const submitOOODates = function(startDate: string, endDate: string) {

        if (!appraiserInfo?.id) {
            return;
        }
        const updateOOO = {
            appraiserId: appraiserInfo.id,
            startDate: startDate,
            endDate: endDate
        };

        setIsLoading(true);
        appraiserApi.updateOOO(updateOOO)
            .then(() => {
                refreshAppraiserData().then(() => {
                    setIsOOOModalOpen(false);
                })
            })
            .catch(console.log)
            .finally(() => setIsLoading(false));
    }

    return (
        <> { appraiserInfo && <>
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Allow Trainees Toggle - Using Headless UI Switch */}
                    <div>
                        <Field>
                            <Label className="block text-sm font-medium text-slate-700">
                                Do you have Trainees?
                            </Label>
                            <div className="mt-2 flex items-center space-x-3">
                                <Switch
                                    checked={haveTrainee}
                                    onChange={handleTraineeToggle}
                                    disabled={!appraiserInfo?.id || isLoading}
                                    className={`${
                                        haveTrainee ? 'bg-blue-600' : 'bg-slate-200'
                                    } relative inline-flex h-6 w-11 items-center rounded-full transition-colors hover:cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                                >
                                    <span
                                        className={`${
                                            haveTrainee ? 'translate-x-6' : 'translate-x-1'
                                        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                                    />
                                </Switch>
                                <p className="text-sm text-slate-500">
                                    Enable if you have a trainee.
                                </p>
                            </div>
                        </Field>
                    </div>

                    {/* Account Security Section */}
                    <div>
                        <label className="block text-sm font-medium text-slate-700">
                            Password Security
                        </label>
                        <div className="mt-1 flex items-center justify-between rounded-md border border-slate-300 bg-slate-50 px-3 py-2">
                            <div className="flex items-center space-x-3">
                                <div className="rounded-full bg-white p-1.5 shadow-sm">
                                    <Key className="h-4 w-4 text-slate-600" />
                                </div>
                                <span className="text-sm text-slate-500">Need to change your password?</span>
                            </div>
                            <button
                                type="button"
                                disabled={isLoading}
                                onClick={() => setIsPasswordModalOpen(true)}
                                className="flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 hover:cursor-pointer hover:bg-slate-100 border border-slate-300 shadow-sm transition-colors">
                                Change
                            </button>
                        </div>
                    </div>
                </div>

                {/* Availability Settings */}
                <div>
                    <label className="block text-sm font-medium text-slate-700">
                        Out of Office Settings
                    </label>
                    <div className="mt-1 flex items-center justify-between rounded-md border border-slate-300 bg-slate-50 px-3 py-2">
                        <div className="flex items-center space-x-3">
                            <div className="rounded-full bg-white p-1.5 shadow-sm">
                                <Calendar className="h-4 w-4 text-slate-600" />
                            </div>
                            <div className="text-sm text-slate-500">
                                <p>Current Out of Office:</p>
                                <p>From: {outOfOffice.startDate}</p>
                                <p>To: {outOfOffice.endDate}</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            disabled={isLoading}
                            onClick={() => setIsOOOModalOpen(true)}
                            className="flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 hover:cursor-pointer hover:bg-slate-100 border border-slate-300 shadow-sm transition-colors"
                        >
                            Set Dates
                        </button>
                    </div>
                </div>
            </div>

            <Modal
                isOpen={isPasswordModalOpen}
                onClose={() => setIsPasswordModalOpen(false)}
                title="Change Password"
            >
                <ChangePassword onSuccess={() => {
                    setIsPasswordModalOpen(false);
                }} />
            </Modal>

            <Modal
                isOpen={isOOOModalOpen}
                onClose={() => setIsOOOModalOpen(false)}
                title="Set Out of Office Dates"
            >
                <SetOutOfOffice
                    onSuccess={submitOOODates}
                    onCancel={() => setIsOOOModalOpen(false)}
                />
            </Modal>
        </>}
        </>
    );
};